import React, { useState } from 'react';
import { X, ThumbsDown, Loader2, Megaphone } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { useToast } from '../../../../src/contexts/ToastContext';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useMotivosPerda } from '../../../../src/hooks/useMotivosPerda';
import { motivosParaEscolha, reabordarEm } from '../../../../src/lib/motivosPerda';

// A lista vem da tabela motivos_perda (#6419); sem ela, a lista padrão de sempre.

interface LossReasonModalProps {
    isOpen: boolean;
    onClose: () => void;
    negotiationId: number;
    userId?: string;
    onConfirm: () => void;
}

const LossReasonModal: React.FC<LossReasonModalProps> = ({ isOpen, onClose, negotiationId, userId, onConfirm }) => {
    const { addToast } = useToast();
    const { motivos, doBanco } = useMotivosPerda();
    const opcoes = motivosParaEscolha(motivos);
    const [motivo, setMotivo] = useState('');
    // Motivo que volta: a perdida cai na lista de Remarketing da BDR. Só avisa com a
    // lista do banco — sem ela, o gatilho que monta a lista também não existe ainda.
    const voltaEm = doBanco ? reabordarEm(opcoes.find(m => m.motivo === motivo)) : null;
    const [observacoes, setObservacoes] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!motivo) {
            addToast('Selecione o motivo da perda.', 'warning');
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase
                .from('negociacoes')
                .update({
                    status: 'perdido',
                    motivo_perda: motivo,
                    observacoes_perda: observacoes || null,
                })
                .eq('id', negotiationId);

            if (error) throw error;

            await logNegotiationAction(negotiationId, userId, 'marcou como perdido', {
                motivo_perda: motivo,
                observacoes_perda: observacoes || null,
            });
            logUsageEvent({ userId, modulo: 'crm', funcionalidade: 'funil', acao: 'marcar_negociacao_perdida', origem: 'acao' });

            addToast('Negociação marcada como perdida.', 'success');
            onConfirm();
            handleClose();
        } catch (error: any) {
            console.error('Erro ao marcar como perdido:', error);
            addToast('Erro ao salvar: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setMotivo('');
        setObservacoes('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in" onClick={handleClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-slide-up border border-gray-100 dark:border-slate-800" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-red-50/50 dark:bg-red-900/10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50">
                            <ThumbsDown size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Motivo da Perda</h2>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Informe o motivo para registrar a perda</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-full transition-colors text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 shadow-sm border border-transparent hover:border-gray-200 dark:hover:border-slate-700"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-2">
                            Motivo *
                        </label>
                        <select
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:border-red-400 focus:ring-1 focus:ring-red-400 outline-none bg-white dark:bg-slate-800 dark:text-white transition-shadow appearance-none cursor-pointer"
                            required
                        >
                            <option value="">Selecione o motivo...</option>
                            {opcoes.map((m) => (
                                <option key={m.motivo} value={m.motivo}>{m.motivo}</option>
                            ))}
                        </select>
                        {voltaEm && (
                            <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500 dark:text-slate-400">
                                <Megaphone size={13} className="mt-0.5 shrink-0 text-secondary" aria-hidden="true" />
                                <span>
                                    Vai para a lista de Remarketing: a BDR retoma o contato a partir de{' '}
                                    <strong className="text-gray-700 dark:text-slate-200">{voltaEm.toLocaleDateString('pt-BR')}</strong>.
                                </span>
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-2">
                            Observações
                        </label>
                        <textarea
                            value={observacoes}
                            onChange={(e) => setObservacoes(e.target.value)}
                            rows={4}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:border-red-400 focus:ring-1 focus:ring-red-400 outline-none bg-white dark:bg-slate-800 dark:text-white transition-shadow resize-none"
                            placeholder="Descreva com mais detalhes o motivo da perda..."
                        />
                    </div>

                    <div className="pt-2 flex justify-end gap-3 border-t border-gray-100 dark:border-slate-800">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-white bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-colors"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                        >
                            {loading && <Loader2 size={16} className="animate-spin" />}
                            Confirmar Perda
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LossReasonModal;
